import * as d3 from 'd3';
import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { API, ShareAPI } from '../../../../API';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../../contexts/CurrentUser';
import { ampli } from '../../../../ampli';
import { Loader } from '../../../../components/Loader/Loader';
import { LoaderSmall } from '../../../../components/LoaderSmall/LoaderSmall';
import Tooltip from '../../../../components/Tooltip/Tooltip';
import { format, parseISO, formatISO } from 'date-fns';
import { ReactComponent as CalendarIcon } from '../../../../assets/calendar.svg';
import { ReactComponent as ViewsIcon } from '../../../../assets/views.svg';
import { ReactComponent as EngagementIcon } from '../../../../assets/engagement.svg';
import { ReactComponent as TranslationIcon } from '../../../../assets/translation.svg';
import { ReactComponent as OriginalTextIcon } from '../../../../assets/originalText.svg';
import { ReactComponent as ExpandIcon } from '../../../../assets/expand.svg';
import { ReactComponent as ActorIcon } from '../../../SearchPage/assets/link.svg';
import { ReactComponent as SourceIcon } from '../../../SearchPage/assets/person.svg';
import { ReactComponent as FollowersIcon } from '../../../../assets/followers.svg';
import { ReactComponent as LinkIcon } from '../../../../assets/link.svg';
import { ReactComponent as SimilarityIcon } from '../../../../assets/similarity.svg';
import { Sentiment } from '../../../../components/Sentiment/Sentiment';
import { ExpandableText } from '../../../../components/CollapsibleText/ExpandableText';
import {
  platformIconsMapping,
  platformNamesMapping,
} from '../../../../utils/platforms';
import {
  contentTypesIconsMapping,
  contentTypesMapping,
} from '../../../../utils/contentTypes';
import styles from '../../../../components/MessagesTable/ExtendedMessageView.module.scss';
// import messageStyles from '../../../../components/MessagesTable/ExtendedMessageView.module.scss';
import { decodeSourceName } from '../../../../utils/decodeURI';
import messageStyles from '../../../NarrativePage/NarrativePage.module.scss';
import { formatWithCustomGigaBillion } from '../../../../utils/fomatNumbers';
import { OriginalTextBlock } from '../../../../components/MessagesTable/OriginalTextBlock';
import { MessageInfoWrapper } from '../../../../components/MessagesTable/MessageInfoWrapper';
import { MessageMetricsWrapper } from '../../../../components/MessagesTable/MessageMetricsWrapper';


export const Messages = ({
  report,
  narrative,
  isSubtitle=false,
  searchQuery,
  start_date,
  end_date,
  statuses,
  sorting = 'engagement',
  page = 1,
  size = 10,
  isShare,
}) => {
  
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState(null);

  const nativeLang = currentUser?.translation_language ? currentUser?.translation_language : window.clientInformation.language.split('-', 1)[0].toUpperCase();

  const fetchNarrativeMessages = useCallback(() => {
    const api = isShare ? ShareAPI : API;

    if (!report || !narrative.id) {
      return
    }

    const urlParams = new URLSearchParams();
    urlParams.set('size', size);
    urlParams.set('page', page);
    urlParams.set('narrative_id', narrative.id);

    if (report.parameters.start_date !== null) {
      urlParams.set(
        'start',
        report.parameters.start_date.split('T')[0] + 'T00:00:00'
      );
    }
    if (report.parameters.end_date !== null) {
      urlParams.set(
        'end',
        report.parameters.end_date.split('T')[0] + 'T23:59:59'
      );
    }

    api.fetch('GET', `/API/v1/messages?${urlParams.toString()}`).then(
      (data) => {
        setMessages(data);
      },
    );
  }, [narrative, page, size, report])

  useEffect(fetchNarrativeMessages, [narrative, size]);

  const [highlights, setHighlights] = useState(['KEYWORDS']);
  const [modalHighlights, setModalHighlights] = useState([]);
  const [highlightsMenu, setHighlightsMenu] = useState(false);

  const statusMapping = {
    NEW: { class: styles.new, name: 'New' },
    READ: { class: styles.approved, name: 'Approved' },
    DELETED: { class: styles.deleted, name: 'Deleted' },
  };

  const translateMessage = (messageId, text, nativeLang, sourceLang='') => {
    let body;
    if(sourceLang) {
      body = {
        text: text,
        destination_language: nativeLang,
        source_language: sourceLang
      }
    } else {
      body = {
        text: text,
        destination_language: nativeLang,
      }
    }
    API.fetch('POST', '/API/v1/translations/translate', null, body).then((data) => {
      setMessages({...messages, objects: messages.objects.map(message => {
      if(message.id === messageId) {
        message.isTranslation = true;
        message.translated = data.destination_text;
        return message;
      } else {
        return message;
      }
      })})
    })
    .catch(e => {
      setMessages({...messages, objects: messages.objects.map(message => {
        if(message.id === messageId) {
          message.isTranslation = true;
          message.error = true;
          message.translated = t('We were not able to translate this text.');
          return message;
        } else {
          return message;
        }
        })})
    });
  }

  const toggleTranslate = (messageId, isTranslation) => {
    setMessages({...messages, objects: messages.objects.map(message => {
      if(message.id === messageId) {
        message.isTranslation = isTranslation;
        return message;
      } else {
        return message;
      }
    })})
  }

  const handleTranslation = (messageId, text, isTranslation) => {
    const target = messages.objects.find(message => message.id === messageId);
    if (target.translated) {
      toggleTranslate(messageId, isTranslation)
    } else {
      toggleTranslate(messageId, true)
      translateMessage(messageId, text, nativeLang)
    }
  }

  if (!narrative) {
    return <Loader />;
  }

  return (
    <>
      <h3>{t('Top publications')}{isSubtitle ? `: ${narrative.name}` : ''}</h3>
      {error ? (
        <div className={styles.noData}>{t(error)}</div>
      ) : !messages ? (
        <Loader />
      ) : messages?.objects?.length > 0 ? (
        <table
          className={`${styles.extendedMessages} ${messageStyles.extendedMessages} ${styles.pdfMode}`}
        >
          <tbody>
            {messages.objects.map((message) => {
              let followers_cnt = message?.source?.audience;

              let textClass = messageStyles.extendedMessage;

              if (message.error && message.isTranslation) {
                textClass = `${messageStyles.extendedMessage} ${styles.error}`;
              } else if (message.error && !message.isTranslation) {
                textClass = messageStyles.extendedMessage;
              }

              // const narrartiveMessage = {
              //   narrative: narrative,
              //   page: page,
              //   searchQuery: searchQuery,
              //   sorting: sorting,
              //   statuses: statuses,
              // };

              return (
                <tr key={message.id}>
                  <td>
                    <MessageInfoWrapper
                      message={message}
                      followers_cnt={followers_cnt}
                      isShare={isShare}
                      currentUser={currentUser}
                      narrative={narrative}
                    />

                    <div className={styles.messageContainer}>
                      <MessageMetricsWrapper
                        message={message}
                        narrative={narrative}
                        isShare={isShare}
                      />

                      <div className={styles.textBlock}>
                        {message.isTranslation === true &&
                        !message.translated ? (
                          <div className={styles.loaderContainer}>
                            <LoaderSmall />
                          </div>
                        ) : (
                          <ExpandableText
                            length={100}
                            highlights={highlights}
                            showFullText={true}
                            text={
                              message.isTranslation
                                ? message.translated
                                : message.highlighted_text || message.text
                            }
                            textClassName={textClass}
                            onExpand={(expanded) => {
                              ampli.track({
                                event_type: expanded
                                  ? 'Expand message in-place'
                                  : 'Collapse message in-place',
                                event_properties: {
                                  user_id: currentUser?.id,
                                  workspace_id: currentUser?.workspace_id,
                                  narrative_id: narrative.id,
                                },
                              });
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="message-controlls">
                    <div className={styles.translationWrapper}>
                      <span
                        className={message?.isTranslation ? '' : styles.active}
                        onClick={() =>
                          handleTranslation(message.id, message.text, false)
                        }
                      >
                        <OriginalTextIcon />
                      </span>
                      <span
                        className={message?.isTranslation ? styles.active : ''}
                        onClick={() =>
                          handleTranslation(message.id, message.text, true)
                        }
                      >
                        <TranslationIcon />
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className={styles.noData}>{t('There is no data to display')}</div>
      )}
    </>
  );
};
